
import React, { useState, useEffect } from "react";
import Layout from "@/components/layout/Layout";
import { Helmet } from "react-helmet";
import { useGuides } from "@/hooks/use-guides";
import { Guide } from "@/types/guides";
import { GuidePreview } from "@/components/guides/GuidePreview";
import { supabase } from "@/integrations/supabase/client"; 
import { GuideDownloadForm } from "@/components/guides/GuideDownloadForm";
import { Session } from "@supabase/supabase-js";
import { GuideHeader } from "@/components/guides/GuideHeader";
import { GuideList } from "@/components/guides/GuideList";
import { GuideDownloadHandler } from "@/components/guides/GuideDownloadHandler";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ArrowRight, Euro, Search, Clock, CheckCircle2, Sparkles } from "lucide-react";

// ── Interactive digital guides ─────────────────────────────────────────────────
const DIGITAL_GUIDES = [
  {
    href: "/guides/finding-nursing-home-ireland-2026",
    badge: "Interactive Guide",
    title: "Finding a Nursing Home in Ireland 2026",
    description:
      "Ireland's most complete guide to choosing the right nursing home — covering care needs, Fair Deal, HIQA ratings, what to look for on visits, and how to make the move. Updated with live 2026 data.",
    chapters: 6,
    highlights: ["Live 2026 sector stats", "6 expandable chapters", "Questions to ask checklist"],
    icon: Search,
    accentClass: "from-primary/8 to-primary/3",
    iconBg: "bg-primary/10",
  },
  {
    href: "/guides/fair-deal-application-ireland-2026",
    badge: "Step-by-Step Guide",
    title: "How to Apply for Fair Deal in Ireland 2026",
    description:
      "Everything families need to navigate the Fair Deal (NHSS) application — 6 clear steps from care assessment to moving in, an interactive documents checklist, financial assessment explained, and live processing stats.",
    chapters: 6,
    highlights: ["Interactive document checklist", "Financial assessment explained", "Common mistakes to avoid"],
    icon: Euro,
    accentClass: "from-primary/5 to-background",
    iconBg: "bg-primary/10",
  },
];


export default function GuidePage() {
  const { guides, isLoading } = useGuides();
  const [session, setSession] = useState<Session | null>(null);
  const [previewGuide, setPreviewGuide] = useState<Guide | null>(null);
  const location = useLocation();
  const navigate = useNavigate();

  // Parse query parameters
  const queryParams = new URLSearchParams(location.search);
  const openDownloadModal = queryParams.get('openDownloadModal') === 'true';
  const guideSlug = queryParams.get('guideSlug');
  
  // Default cover image for guides without covers
  const defaultCoverImage = "/lovable-uploads/0af25d51-4e55-4b75-8948-ba6ef7e018b7.png";

  // Fetch session on component mount
  useEffect(() => {
    async function getSession() {
      const { data } = await supabase.auth.getSession();
      setSession(data.session);
    }
    
    getSession();
    
    // Set up auth state change listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        setSession(session);
      }
    );
    
    return () => subscription.unsubscribe();
  }, []);

  // Get download handling logic from our custom handler
  const { handleDownload, downloadFormGuide, setDownloadFormGuide } = GuideDownloadHandler({ session });

  // Handle guide download request from URL parameters
  useEffect(() => {
    if (openDownloadModal && guideSlug && guides.length > 0) {
      // Find the guide by slug
      const guideToDownload = guides.find(g => g.slug === guideSlug);
      
      if (guideToDownload) {
        // Open download modal
        setDownloadFormGuide(guideToDownload);
        
        // Clean up URL without reloading the page
        const newUrl = window.location.pathname;
        window.history.replaceState({}, document.title, newUrl);
      } else if (guideSlug) {
        // If we have a specific guide slug but couldn't find it, redirect to its detail page
        navigate(`/guides/${guideSlug}?openDownloadModal=true`);
      }
    }
  }, [guides, guideSlug, openDownloadModal, navigate, setDownloadFormGuide]);

  // Check for guide download request from session storage (for backward compatibility)
  useEffect(() => {
    const openDownloadModal = sessionStorage.getItem('openDownloadModal');
    const downloadGuideId = sessionStorage.getItem('downloadGuideId');
    
    if (openDownloadModal === 'true' && downloadGuideId && guides.length > 0) {
      // Find the guide by ID
      const guideToDownload = guides.find(g => g.id === downloadGuideId);
      
      if (guideToDownload) {
        setDownloadFormGuide(guideToDownload);
        // Clear the session storage flags
        sessionStorage.removeItem('openDownloadModal');
        sessionStorage.removeItem('downloadGuideId');
      }
    }
  }, [guides, setDownloadFormGuide]);

  const handlePreview = (e: React.MouseEvent, guide: Guide) => {
    e.preventDefault();
    setPreviewGuide(guide);
  };

  return (
    <Layout>
      <Helmet>
        <title>Guides & Resources | Grandpal — Irish Nursing Home Guides 2026</title>
        <meta name="description" content="Free guides to help Irish families navigate nursing home decisions in 2026. Covers Fair Deal application, finding the right home, HIQA, and costs." />
      </Helmet>
      
      <div className="bg-gradient-to-b from-primary/5 to-background py-12 md:py-20 border-b border-border">
        <div className="container max-w-5xl px-4">
          <div className="text-center mb-10 md:mb-14">
            <div className="flex items-center justify-center gap-2 mb-3">
              <Sparkles className="h-4 w-4 text-primary" />
              <span className="text-sm font-medium text-primary uppercase tracking-wider">Resources Hub</span>
            </div>
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-foreground mb-4">
              Guides & Resources
            </h1>
            <p className="text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto">
              Free, plain-English guides to help Irish families navigate nursing home decisions in 2026 — from Fair Deal to finding the right home.
            </p>
          </div>

          {/* ── Digital interactive guides ── */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6 mb-6">
            {DIGITAL_GUIDES.map((guide) => {
              const Icon = guide.icon;
              return (
                <Link
                  key={guide.href}
                  to={guide.href}
                  className={`group rounded-2xl border border-border bg-gradient-to-br ${guide.accentClass} p-5 sm:p-6 flex flex-col gap-4 hover:shadow-md transition-shadow`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className={`${guide.iconBg} rounded-full p-2.5 shrink-0`}>
                      <Icon className="h-5 w-5 text-primary" />
                    </div>
                    <Badge variant="secondary" className="text-xs shrink-0">{guide.badge}</Badge>
                  </div>

                  <div className="flex-1">
                    <h2 className="text-base sm:text-lg font-bold text-foreground mb-2 leading-snug group-hover:text-primary transition-colors">
                      {guide.title}
                    </h2>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {guide.description}
                    </p>
                  </div>

                  <ul className="space-y-1.5">
                    {guide.highlights.map((h) => (
                      <li key={h} className="flex items-center gap-2 text-xs text-muted-foreground">
                        <CheckCircle2 className="h-3.5 w-3.5 text-primary shrink-0" />
                        {h}
                      </li>
                    ))}
                  </ul>

                  <div className="flex items-center gap-1.5 text-sm font-medium text-primary">
                    Read guide
                    <ArrowRight className="h-3.5 w-3.5 group-hover:translate-x-1 transition-transform" />
                  </div>
                </Link>
              );
            })}
          </div>

          {/* Stats strip */}
          <div className="grid grid-cols-3 gap-3 sm:gap-4">
            {[
              { icon: Clock, label: "Updated for 2026", desc: "Live data & current figures" },
              { icon: CheckCircle2, label: "100% free", desc: "No signup required" },
              { icon: Sparkles, label: "AI-powered stats", desc: "Sourced from HSE, HIQA & NTPF" },
            ].map((item) => (
              <div key={item.label} className="flex flex-col sm:flex-row items-start sm:items-center gap-2 p-3 sm:p-4 rounded-xl border border-border bg-card">
                <item.icon className="h-4 w-4 text-primary shrink-0" />
                <div>
                  <p className="text-xs font-semibold text-foreground">{item.label}</p>
                  <p className="text-xs text-muted-foreground hidden sm:block mt-0.5">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Legacy DB guides ── */}
      <div className="bg-background py-14 md:py-20">
        <div className="container max-w-5xl px-4">
          {guides.length > 0 && (
            <>
              <h2 className="text-xl sm:text-2xl font-bold text-foreground mb-8">
                Downloadable Resources
              </h2>
              <GuideList 
                guides={guides}
                isLoading={isLoading}
                defaultCoverImage={defaultCoverImage}
                onPreview={handlePreview}
                onDownload={handleDownload}
              />
            </>
          )}
          {isLoading && (
            <GuideList 
              guides={guides}
              isLoading={isLoading}
              defaultCoverImage={defaultCoverImage}
              onPreview={handlePreview}
              onDownload={handleDownload}
            />
          )}
        </div>
      </div>

      {previewGuide && (
        <GuidePreview 
          guide={previewGuide} 
          onClose={() => setPreviewGuide(null)} 
        />
      )}

      {downloadFormGuide && (
        <GuideDownloadForm
          guide={downloadFormGuide}
          open={!!downloadFormGuide}
          onOpenChange={(open) => !open && setDownloadFormGuide(null)}
        />
      )}

      <div className="w-full bg-background py-16 md:py-24">
        <img 
          src="/lovable-uploads/47f2aef4-6ba9-49f0-a0ee-dd0d0dc05675.png" 
          alt="Flower Activity" 
          className="w-full max-h-[400px] object-contain"
        />
      </div>
    </Layout>
  );
}
